import { numberWithCommas } from "js/utils";

export const normalizeFloat = (decimalNumbers = 2) => (value = "") => {
  if (!value && value !== 0) return "";

  let normalized = value.toString().trim();
  if (!normalized) return "";

  // Extraer y preservar el signo negativo
  const isNegative = normalized.startsWith("-");
  if (isNegative) normalized = normalized.slice(1);
  if (!normalized) return isNegative ? "-" : "";

  // Para números enteros (decimalNumbers = 0), todos los puntos son separadores de miles
  if (decimalNumbers === 0) {
    // Eliminar todos los puntos (son separadores de miles) y comas (no válidas para enteros)
    normalized = normalized.replace(/[.,]/g, "");
    // Eliminar todo lo que no sea dígito
    normalized = normalized.replace(/[^0-9]/g, "");
    return isNegative ? "-" + normalized : normalized;
  }

  // Para números decimales, lógica existente
  // Detectar si hay coma (separador decimal preferido)
  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma) {
    // Coma como separador decimal: eliminar todos los puntos (miles)
    normalized = normalized.replace(/\./g, "");
    normalized = normalized.replace(",", ".");
  } else if (hasDot) {
    // Punto: si hay múltiples, el último es decimal, los demás son miles
    const lastDotIndex = normalized.lastIndexOf(".");
    if (lastDotIndex !== normalized.indexOf(".")) {
      // Múltiples puntos: eliminar todos y dejar solo el último
      const beforeLastDot = normalized
        .slice(0, lastDotIndex)
        .replace(/\./g, "");
      const afterLastDot = normalized.slice(lastDotIndex + 1);
      normalized = beforeLastDot + "." + afterLastDot;
    }
  }

  // Eliminar todo lo que no sea dígito o punto decimal
  normalized = normalized.replace(/[^0-9.]/g, "");

  // No permitir separador sin dígitos
  const digitsCount = (normalized.match(/[0-9]/g) || []).length;
  if (digitsCount === 0) {
    return normalized.includes(".")
      ? ""
      : isNegative
        ? "-" + normalized
        : normalized;
  }

  const length = normalized.length - 1;

  // Si termina en punto, devolver con coma para continuar escribiendo
  if (normalized[length] === ".") {
    return (isNegative ? "-" : "") + normalized.slice(0, -1) + ",";
  }

  // NO aplicar toFixed mientras el usuario está escribiendo
  // Solo devolver el valor normalizado tal cual
  return isNegative ? "-" + normalized : normalized;
};

export const formatFloat = (decimalNumbers = 2) => (value = "", name) => {
  if (value === "-") return value;
  if (!value && value !== 0) return undefined;

  let val = value.toString().trim();
  if (!val || !val.length) return undefined;

  // Extraer y preservar el signo negativo
  const isNegative = val.startsWith("-");
  if (isNegative) val = val.slice(1);
  if (!val) return isNegative ? "-" : undefined;

  // Para números enteros (decimalNumbers = 0), todos los puntos son separadores de miles
  if (decimalNumbers === 0) {
    // Eliminar puntos y comas, solo quedarse con dígitos
    const cleaned = val.replace(/[.,]/g, "").replace(/[^0-9]/g, "");
    if (!cleaned) return undefined;
    const num = parseInt(cleaned, 10);
    if (isNaN(num)) return "";
    // Formatear con puntos de miles pero sin decimales
    return isNegative
      ? "-" + numberWithCommas(num, 0)
      : numberWithCommas(num, 0);
  }

  // Para números decimales, lógica existente
  const endsWithComma = val[val.length - 1] === ",";
  const raw = endsWithComma ? val.slice(0, -1) : val;

  // Detectar si tiene formato con puntos (miles) y coma (decimal)
  const hasComma = raw.includes(",");
  const hasDot = raw.includes(".");

  let cleaned;
  if (hasComma) {
    // Tiene coma: eliminar puntos (miles) y convertir coma a punto para parseFloat
    cleaned = raw.replace(/\./g, "").replace(",", ".");
  } else if (hasDot) {
    // El valor viene de normalizeFloat que SIEMPRE usa punto como decimal
    // Eliminar puntos de miles (todos menos el último) y mantener el último como decimal
    const lastDotIndex = raw.lastIndexOf(".");
    const beforeLastDot = raw.slice(0, lastDotIndex).replace(/\./g, "");
    const afterLastDot = raw.slice(lastDotIndex + 1);
    cleaned = beforeLastDot + "." + afterLastDot;
  } else {
    // No tiene separadores, es un entero
    cleaned = raw;
  }

  const num = parseFloat(cleaned);

  if (isNaN(num)) return "";

  // Preservar ceros trailing en la parte decimal mientras el usuario tipea
  // Ej: "0.00" no debe colapsar a "0" porque el usuario está escribiendo "0,007"
  if (cleaned.includes(".")) {
    const [intPart, decPart] = cleaned.split(".");
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    const trimmedDec = decPart.slice(0, decimalNumbers);
    const result = endsWithComma
      ? formattedInt + "," + trimmedDec + ","
      : formattedInt + "," + trimmedDec;
    return isNegative ? "-" + result : result;
  }

  const formatted = endsWithComma
    ? numberWithCommas(num, decimalNumbers) + ","
    : numberWithCommas(num, decimalNumbers);
  return isNegative ? "-" + formatted : formatted;
};

export const intParser = (value = "") => {
  // Manejar valores null/undefined/vacíos
  if (!value && value !== 0) return null;

  // Eliminar puntos (miles) y comas, luego parsear
  const val = value
    .toString()
    .replace(/\./g, "")
    .replace(/,/g, "");
  return !isNaN(parseInt(val, 10)) ? parseInt(val, 10) : null;
};

export const floatParser = (value = "") => {
  if (!value && value !== 0) return null;

  // Convertir a string y limpiar espacios
  const str = value.toString().trim();
  if (!str) return null;

  // Detectar si hay coma (separador decimal en español)
  const hasComma = str.includes(",");

  let cleaned;
  if (hasComma) {
    // Formato español: eliminar puntos (miles) y convertir coma a punto (decimal)
    cleaned = str.replace(/\./g, "").replace(",", ".");
  } else {
    // Si solo hay puntos, el último podría ser decimal o todos son miles
    // Para ser seguros, eliminar todos los puntos excepto el último si hay dígitos después
    const lastDotIndex = str.lastIndexOf(".");
    if (lastDotIndex >= 0) {
      const afterLastDot = str.slice(lastDotIndex + 1);
      // Si hay 1-2 dígitos después del último punto, es decimal
      if (afterLastDot.length > 0 && afterLastDot.length <= 2) {
        const beforeLastDot = str.slice(0, lastDotIndex).replace(/\./g, "");
        cleaned = beforeLastDot + "." + afterLastDot;
      } else {
        // Todos son separadores de miles
        cleaned = str.replace(/\./g, "");
      }
    } else {
      cleaned = str;
    }
  }

  const num = parseFloat(cleaned);
  return !isNaN(num) ? num : null;
};

export const normalizeFloat_V2 = (decimalNumbers = 2) => (value = "") => {
  if (!value && value !== 0) return "";

  let normalized = value.toString().trim();
  if (!normalized) return "";

  // Si no hay coma pero hay puntos, verificar si son miles
  if (!normalized.includes(",")) {
    const lastDotIndex = normalized.lastIndexOf(".");
    if (lastDotIndex >= 0) {
      const afterLastDot = normalized.slice(lastDotIndex + 1);
      // Heurística de floatParser: si hay más de 2 dígitos tras el punto, son miles
      if (afterLastDot.length > 2) {
        normalized = normalized.replace(/\./g, "");
      }
    }
  } else {
    // Si hay coma, todos los puntos son miles
    normalized = normalized.replace(/\./g, "");
  }

  // A partir de aquí, seguir lógica estándar de normalizeFloat
  const hasComma = normalized.includes(",");
  const hasDot = normalized.includes(".");

  if (hasComma) {
    normalized = normalized.replace(/\./g, "");
    normalized = normalized.replace(",", ".");
  } else if (hasDot) {
    const lastDotIndex = normalized.lastIndexOf(".");
    if (lastDotIndex !== normalized.indexOf(".")) {
      const beforeLastDot = normalized
        .slice(0, lastDotIndex)
        .replace(/\./g, "");
      const afterLastDot = normalized.slice(lastDotIndex + 1);
      normalized = beforeLastDot + "." + afterLastDot;
    }
  }

  normalized = normalized.replace(/[^0-9.]/g, "");
  const digitsCount = (normalized.match(/[0-9]/g) || []).length;
  if (digitsCount === 0) {
    return normalized.includes(".") ? "" : normalized;
  }

  const length = normalized.length - 1;
  if (normalized[length] === ".") {
    return normalized.slice(0, -1) + ",";
  }

  return normalized;
};

export const formatFloat_V2 = (decimalNumbers = 2) => (value = "", name) => {
  if (value === "-") return value;
  if (!value && value !== 0) return undefined;

  let val = value.toString().trim();
  if (!val || !val.length) return undefined;

  // Usar una lógica similar a floatParser para limpiar antes de reformatear
  const hasComma = val.includes(",");
  let cleaned;

  if (hasComma) {
    cleaned = val.replace(/\./g, "").replace(",", ".");
  } else {
    const lastDotIndex = val.lastIndexOf(".");
    if (lastDotIndex >= 0) {
      const afterLastDot = val.slice(lastDotIndex + 1);
      if (afterLastDot.length > 0 && afterLastDot.length <= 2) {
        const beforeLastDot = val.slice(0, lastDotIndex).replace(/\./g, "");
        cleaned = beforeLastDot + "." + afterLastDot;
      } else {
        cleaned = val.replace(/\./g, "");
      }
    } else {
      cleaned = val;
    }
  }

  const num = parseFloat(cleaned);
  if (isNaN(num)) return "";

  const endsWithComma = val[val.length - 1] === ",";

  return endsWithComma
    ? numberWithCommas(num, decimalNumbers) + ","
    : numberWithCommas(num, decimalNumbers);
};
